/**
 * File Data Service
 * Centralized service for file data operations
 */

import { BASE_URL, pythonBaseUrl } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";

/**
 * Update file data in the database
 * @param fileData - The file data object to update
 * @returns Promise with the response
 */
export const updateFileData = async (fileData: any) => {
  try {
    const response = await fetch(`${BASE_URL}/FileData/ImportFiles`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
      body: JSON.stringify(fileData),
    });

    if (!response.ok) {
      throw new Error(`Failed to update file data: ${response.status} - ${response.statusText}`);
    }

    return {
      success: true,
      response,
    };
  } catch (error) {
    console.error("❌ Error updating file data:", error);
    throw error;
  }
};

/**
 * Update multiple file data records
 * @param fileDataArray - Array of file data objects to update
 * @returns Promise with results for each update
 */
export const updateMultipleFileData = async (fileDataArray: any[]) => {
  try {
    const updatePromises = fileDataArray.map((fileData) => updateFileData(fileData));
    const results = await Promise.all(updatePromises);

    return {
      success: true,
      results,
    };
  } catch (error) {
    console.error("❌ Error updating multiple file data:", error);
    throw error;
  }
};

/**
 * Update file data with custom error handling
 * @param fileData - The file data object to update
 * @param onSuccess - Optional success callback
 * @param onError - Optional error callback
 */
export const updateFileDataWithCallbacks = async (
  fileData: any,
  onSuccess?: (response: Response) => void,
  onError?: (error: Error) => void
) => {
  try {
    const result = await updateFileData(fileData);

    if (onSuccess && result.response) {
      onSuccess(result.response);
    }

    return result;
  } catch (error) {
    if (onError) {
      onError(error as Error);
    }
    throw error;
  }
};

/**
 * Upload file to server
 * @param formData - FormData object containing the file and metadata
 * @returns Promise with the upload response data
 */
export const uploadFile = async (formData: FormData) => {
  try {
    const response = await fetch(`${pythonBaseUrl}/File/UploadFile`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`File upload failed with status ${response.status}`);
    }

    const responseData = await response.json();

    return {
      success: true,
      data: responseData,
      response,
    };
  } catch (error) {
    console.error("❌ Error uploading file:", error);
    throw error;
  }
};

/**
 * Import file data to database (POST method)
 * @param fileDataArray - Array of file data objects to import
 * @returns Promise with the import response data
 */
export const importFileData = async (fileDataArray: any[]) => {
  try {
    const response = await fetch(`${BASE_URL}/filedata/importfile`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getUserIdFromLocalStorage().token}`,
      },
      body: JSON.stringify(fileDataArray),
    });

    if (!response.ok) {
      throw new Error("Failed to import file data to database");
    }

    const data = await response.json();

    return {
      success: true,
      data,
      response,
    };
  } catch (error) {
    console.error("❌ Error importing file data:", error);
    throw error;
  }
};

const FileDataService = {
  updateFileData,
  updateMultipleFileData,
  updateFileDataWithCallbacks,
  uploadFile,
  importFileData,
};

export default FileDataService;
